import { buildGeocodeInputText, deriveResolvedNameFromLabel, getCustomerPosition, isExactLocalityMatch, isReliableGeocode, needsGeocodeConfirmation, } from "../geo/services.js";
/**
 * `!isReliableGeocode` sur la position stockée de la fiche (#797) — délègue
 * entièrement le critère ; voir `isReliableGeocode` (`geo/services.ts`) pour
 * la sémantique complète, notamment `precision = null`.
 */
export const isInvalid = (customer) => {
    // `getCustomerPosition` porte déjà le parsing string → number des colonnes stockées.
    const position = getCustomerPosition(customer);
    return !isReliableGeocode(customer.serviceGeocodePrecision, position?.latitude, position?.longitude);
};
/**
 * Seules granularités que le listing traite comme confirmées — allow-list,
 * pas deny-list (doctrine `docs/conventions/backend.md` § Doctrine de gate) :
 * `GEOCODE_PRECISION_VALUES` a déjà grandi deux fois (`toponym`, `unknown`),
 * une deny-list des précisions "à vérifier" échouerait ouvert (silencieuse)
 * sur une future valeur, exactement la classe de bug de ce ticket.
 *
 * `toponym`/`locality` font partie de l'allow-list (#1082) : ce sont les
 * mêmes granularités que `useAddressVerification.status` traite comme
 * éligibles à `"recognized"`/`"close-match"` (concordance ou égalité exacte
 * saisie↔résolu) — un lieu-dit résolu ne s'affiche pas en rouge alors que le
 * formulaire fiche client le traite comme fiable.
 */
const EXPLOITABLE_LIST_PRECISIONS = new Set([
    "housenumber",
    "street",
    "toponym",
    "locality",
]);
/**
 * Verdict commun aux deux prédicats ci-dessous, sur les trois court-circuits
 * qui ne dépendent QUE de la precision/coordonnées/pin manuel — jamais du
 * `service_geocode_label`. `"exploitable"` ne dit PAS confirmé : c'est le cas
 * où la precision seule ne suffit pas à trancher, laissé à la charge de
 * chaque appelant (voir `isServiceAddressUnconfirmedInList` vs
 * `isServiceAddressUnconfirmedForDisplay` ci-dessous — c'est précisément
 * le point de DÉCOUPLAGE entre les deux, #1086).
 */
const resolveStoredPrecisionVerdict = (customer) => {
    const position = getCustomerPosition(customer);
    if (!Number.isFinite(position?.latitude) || !Number.isFinite(position?.longitude)) {
        return "unconfirmed";
    }
    if (customer.serviceGeocodeSource === "manual") {
        return "confirmed";
    }
    const precision = customer.serviceGeocodePrecision;
    if (precision == null) {
        return "confirmed";
    }
    return EXPLOITABLE_LIST_PRECISIONS.has(precision) ? "exploitable" : "unconfirmed";
};
/**
 * Signal DB : pilote une écriture réelle du batch #1037
 * (`classifyAddressResync`/`storedOk`, `packages-api/platform-server/src/geo/
 * addressResyncPass.ts`). Rouge = précision non exploitable (tout sauf
 * `housenumber`/`street`/`toponym`/`locality`) OU coordonnées absentes —
 * NE lit PAS `service_geocode_label` : la precision seule tranche.
 *
 * Un pin posé à la main (`serviceGeocodeSource === "manual"`, porte de
 * sortie #799), avec des coordonnées exploitables, est TOUJOURS considéré
 * confirmé, quelle que soit sa `precision` stockée : `CustomerService.
 * update()` persiste `"unknown"` pour tout pin manuel (aucune granularité IGN
 * à rapporter sur un point posé par un humain) — sans ce court-circuit, une
 * adresse déjà validée manuellement resterait signalée en rouge sans aucun
 * geste possible pour l'en sortir. Le court-circuit ne porte QUE sur la
 * precision, jamais sur les coordonnées : une fiche `manual` sans coordonnées
 * exploitables (ex. création depuis un export qui pose la marque sans
 * fournir de lat/long, `CustomerService.save()`, #799) doit rester signalée —
 * `serviceGeocodeSource` seul ne garantit pas une position exploitable.
 *
 * ⚠️ Garde-fou anti-régression, ne PAS fusionner avec `isInvalid` :
 * 1. `isInvalid`/`isReliableGeocode` est un gate métier partagé par 3
 *    consommateurs qui doivent accepter `toponym`/`locality`/`unknown` — le
 *    tunnel RDV public (`rdv/matchEmail.ts`), la réutilisation d'adresse à la
 *    soumission RDV (`rdvRequest.service.ts`), et le gate de facturation REAL
 *    (`custom.service.ts`).
 * 2. Fusionner re-refuserait la facturation du déplacement pour les fiches en
 *    `unknown` (résolu IGN sans granularité rapportée) — une régression
 *    silencieuse de la doctrine `docs/context/geocodage.md` §2. `toponym`/
 *    `locality` sont dans l'allow-list ci-dessus (#1082) : les deux
 *    prédicats s'accordent sur ces deux granularités, seul `unknown` reste
 *    un cas où ils divergent (`municipality` est refusé par les deux,
 *    jamais de divergence).
 * 3. ⚠️ **NE PAS étendre cette fonction au STOCKÉ « proche trouvée » (#1086)**
 *    — c'est précisément le prédicat que `classifyAddressResync` lit pour
 *    décider d'écrire en base. `docs/context/geocodage.md` §7 documente le
 *    garde-fou : le signal d'affichage renforcé de `isServiceAddressUnconfirmedForDisplay`
 *    est un prédicat SÉPARÉ, jamais celui-ci — un prédicat unique ferait
 *    entrer des fiches « proche trouvée » dans `write-improve`/sortir de
 *    `write-degrade`, contredisant l'AC d'invariant du ticket.
 */
export const isServiceAddressUnconfirmedInList = (customer) => resolveStoredPrecisionVerdict(customer) === "unconfirmed";
/**
 * Signal d'AFFICHAGE du listing clients (`CustomerList.tsx` via
 * `getCustomerClass.ts`) — seul consommateur. Étend le verdict précédent :
 * une fiche de precision exploitable peut quand même être « proche trouvée »
 * si le nom de voie/lieu résolu — extrait du `service_geocode_label` stocké
 * via `deriveResolvedNameFromLabel` — diverge en réalité de l'adresse
 * saisie (#1086). Même verdict que le LIVE (`resolveLiveAddressStatus`,
 * `addressResyncPass.ts`) — containment + bypass mono-token sur un lieu-dit
 * exactement concordant — appliqué ici au STOCKÉ plutôt qu'à un
 * re-géocodage.
 *
 * Découplée à dessein d'`isServiceAddressUnconfirmedInList` (voir son
 * garde-fou n°3) : cette extension ne doit JAMAIS atteindre le batch #1037,
 * qui continue de lire l'autre prédicat, inchangé.
 */
export const isServiceAddressUnconfirmedForDisplay = (customer) => {
    const verdict = resolveStoredPrecisionVerdict(customer);
    if (verdict !== "exploitable") {
        return verdict === "unconfirmed";
    }
    return needsStoredAddressConfirmation(customer, customer.serviceGeocodePrecision);
};
/**
 * Résolu vide (label absent/vide/mal formé, ou CP extrait divergent du CP
 * stocké) traité à part, AVANT `isExactLocalityMatch` : sans ce court-circuit,
 * un lieu-dit sans label et une saisie elles-mêmes vides matcheraient
 * trivialement (deux chaînes vides normalisées sont égales) — l'AC « rouge
 * par prudence sur résolu vide » (#1086) doit tenir sans dépendre de l'ordre
 * des OR ci-dessous.
 */
const needsStoredAddressConfirmation = (customer, precision) => {
    const resolvedName = deriveResolvedNameFromLabel(customer.serviceGeocodeLabel, customer.serviceZip);
    if (resolvedName === "") {
        return true;
    }
    const fields = {
        line1: customer.serviceAddress ?? "",
        line2: customer.serviceAddress2,
        zip: customer.serviceZip ?? "",
        city: customer.serviceCity ?? "",
    };
    if (isExactLocalityMatch(precision, resolvedName, fields.line1)) {
        return false;
    }
    return needsGeocodeConfirmation(precision, resolvedName, buildGeocodeInputText(fields));
};
export const getLabel = (customer, displayTargets) => {
    if (!customer) {
        return '""';
    }
    let result = customer.companyLegalName
        ? customer.companyLegalName
        : [customer.familyName, customer.firstName].filter(Boolean).join(" ");
    if (displayTargets &&
        "serviceTargets" in customer &&
        customer.serviceTargets &&
        customer.serviceTargets.length > 0) {
        result += ` (${customer.serviceTargets.map((serviceTarget) => serviceTarget.label).join(", ")})`;
    }
    return result;
};
const COMBINING_DIACRITICS = /\p{Mn}/gu;
const NON_ALPHANUM = /[^a-z0-9]+/g;
const SURROUNDING_DASHES = /^-+|-+$/g;
/**
 * Build a filename-safe slug from a customer. Used when constructing PDF filenames
 * (invoices, quotations, reports, certificates). Unlike `getLabel`, the output is
 * ASCII, lowercase, dash-separated, and never produces "null"/"undefined" segments.
 *
 * Source priority: companyLegalName, otherwise "familyName firstName".
 * Falls back to "client" when no usable source is found.
 */
export const getCustomerFilenameSlug = (customer) => {
    const source = customer?.companyLegalName
        ? customer.companyLegalName
        : [customer?.familyName, customer?.firstName].filter(Boolean).join(" ");
    const slug = source
        .normalize("NFD")
        .replace(COMBINING_DIACRITICS, "")
        .toLowerCase()
        .replace(NON_ALPHANUM, "-")
        .replace(SURROUNDING_DASHES, "");
    return slug || "client";
};
export function withCustomerServiceAddress(customer, address) {
    if (address === undefined) {
        return customer;
    }
    return {
        ...customer,
        serviceAddress: address?.address ?? "",
        serviceAddress2: address?.address2 ?? null,
        serviceCity: address?.city ?? "",
        serviceZip: address?.zip ?? "",
        serviceLat: address?.latitude ?? null,
        serviceLong: address?.longitude ?? null,
        serviceGeocodeScore: address?.geocodeScore ?? null,
        serviceGeocodePrecision: address?.geocodePrecision ?? null,
        serviceGeocodeLabel: address?.geocodeLabel ?? null,
        serviceGeocodeSource: address?.geocodeSource ?? null,
    };
}
//# sourceMappingURL=services.js.map